import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { queryJobRuns, queryJobs } from '@/features/jobs/data/jobs';
import { buildJobHistoryRows } from '@/features/jobs/hooks/jobHistoryRows';
import * as jobsPageQueryGates from '@/features/jobs/hooks/jobsPageQueryGates';
import { useJobsPageFilterOptionCounts } from '@/features/jobs/hooks/useJobsPageFilterOptionCounts';
import { useJobsPageOptionQueries } from '@/features/jobs/hooks/useJobsPageOptionQueries';
import {
  buildFallbackJobRuns,
  buildJobCounts,
  buildJobMetricCards,
  buildJobRunMetricBuckets,
  buildJobRunMetricSummary,
  buildJobStatusCountsByDate,
  buildJobStatusSeries,
  filterJobsBySearch,
  filterJobRunsBySearch,
  formatSelectedDateRangeLabel,
  resolveLatestBuildDay,
  resolveVisibleBuildStatuses,
  sortHistoryRows,
} from '@/features/jobs/hooks/useJobsPageViewModel.helpers';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectMetricThresholds } from '@/features/projects';
import {
  formatRangeWindowNoDataText,
  useLazyComparisonDateRange,
  useQualityDateRange,
} from '@/features/quality/hooks';
import { buildBuildMetricOptions } from '@/features/quality/sections/qualityMetricOptions';
import {
  buildMetricsChartModelFromBuckets,
  buildMetricsChartToolbarState,
} from '@/features/shared/components/chartPanels/chartPanelAdapters';
import {
  useDailyHistoryPanelPageReset,
  useDailyHistoryPanelState,
} from '@/features/shared/hooks/useDailyHistoryPanelState';
import { useScopedProjectFilter } from '@/features/shared/hooks/useScopedProjectFilter';
import {
  buildChartDatesForDayRange,
  resolveWindowDayKeys,
} from '@/features/shared/utils/chartDateWindow';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import {
  gridPaths,
  queryKeys,
  useIsAuthenticated,
  usePersistedStringArray,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { JobSortKey, RunSortKey } from '@/features/jobs/components/JobsTablePanel';
import type { UseJobsPageViewModelResult } from '@/features/jobs/hooks/useJobsPageViewModel.types';
import type { StackedMetricsStatusChartSectionProps } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type { BuildStatus } from '@flowqa/shared';

export const useJobsPageViewModel = (): UseJobsPageViewModelResult => {
  const [searchParams] = useSearchParams();
  const {
    activateComparisonForRange,
    comparisonActivated,
    handleEndDayChange,
    handleStartDayChange,
    range,
    selectedEndDay,
    selectedStartDay,
  } = useLazyComparisonDateRange('7d');
  const [activeTab, setActiveTab] = useState<'jobs' | 'runs'>('jobs');
  const [jobSearchValue, setJobSearchValue] = useState('');
  const [runSearchValue, setRunSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<BuildStatus[]>([]);
  const [selectedPipelineIds, setSelectedPipelineIds] = useState<string[]>([]);
  const [selectedStageNames, setSelectedStageNames] = useState<string[]>([]);
  const [selectedJobNames, setSelectedJobNames] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:jobs');
  const {
    page: historyPage,
    pageSize: historyPageSize,
    sortKey: historySortKey,
    sortDirection: historySortDirection,
    isTransitionLoading: isHistoryTransitionLoading,
    setPage: setHistoryPage,
    setPageSize: setHistoryPageSize,
    setSortKey: setHistorySortKey,
    setSortDirection: setHistorySortDirection,
  } = useDailyHistoryPanelState({
    defaultSortKey: 'pipelineLabel',
  });
  const [jobColumnVisibilityModel, setJobColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      jobName: true,
      runTotal: true,
      status: true,
      metricsSummary: true,
    });
  const [jobSortKey, setJobSortKey] = useState<JobSortKey>('jobName');
  const [jobSortDirection, setJobSortDirection] = useState<'asc' | 'desc'>('asc');
  const [runColumnVisibilityModel, setRunColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      pipelineName: true,
      buildKey: true,
      status: true,
    });
  const [runSortKey, setRunSortKey] = useState<RunSortKey>('startedAt');
  const [runSortDirection, setRunSortDirection] = useState<'asc' | 'desc'>('desc');
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useQualityDateRange(range);
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const {
    effectiveProjectId,
    handleProjectsChange,
    projectFilterOptions,
    selectedProjects,
    showProjectFilter,
  } = useScopedProjectFilter({ enabled: selectedOrganisationId !== null });
  const { tagOptions } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated,
  });
  const { thresholds } = useProjectMetricThresholds(effectiveProjectId);
  const isLatestMode = range === 'latest';
  const showDelta = range !== 'latest' && previousRangeStartIso !== null;
  const rangeStartDay = useMemo(() => dayjs(rangeStartIso).format('YYYY-MM-DD'), [rangeStartIso]);
  const rangeEndDay = useMemo(() => dayjs(rangeEndIso).format('YYYY-MM-DD'), [rangeEndIso]);
  const chartDates = useMemo(
    () =>
      buildChartDatesForDayRange(selectedStartDay ?? rangeStartDay, selectedEndDay ?? rangeEndDay),
    [rangeEndDay, rangeStartDay, selectedEndDay, selectedStartDay],
  );
  const historyDayKeys = useMemo(
    () =>
      resolveWindowDayKeys({
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        fallbackStartDay: rangeStartDay,
        fallbackEndDay: rangeEndDay,
        descending: true,
      }),
    [rangeEndDay, rangeStartDay, selectedEndDay, selectedStartDay],
  );
  const jobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('pipelineId', selectedPipelineIds),
        buildInFilter('stageName', selectedStageNames),
        buildInFilter('jobName', selectedJobNames),
        buildInFilter('tags', selectedTags),
        buildDateRangeFilters(
          'startedAt',
          selectedStartDay ? dayjs(selectedStartDay).startOf('day').toISOString() : rangeStartIso,
          selectedEndDay ? dayjs(selectedEndDay).endOf('day').toISOString() : rangeEndIso,
        ),
      ),
    [
      rangeEndIso,
      rangeStartIso,
      selectedEndDay,
      selectedJobNames,
      selectedPipelineIds,
      selectedStageNames,
      selectedStartDay,
      selectedStatuses,
      selectedTags,
    ],
  );
  const previousJobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('pipelineId', selectedPipelineIds),
        buildInFilter('stageName', selectedStageNames),
        buildInFilter('jobName', selectedJobNames),
        buildInFilter('tags', selectedTags),
        buildDateRangeFilters('startedAt', previousRangeStartIso, previousRangeEndIso),
      ),
    [
      previousRangeEndIso,
      previousRangeStartIso,
      selectedJobNames,
      selectedPipelineIds,
      selectedStageNames,
      selectedStatuses,
      selectedTags,
    ],
  );
  const currentJobsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/page',
      'current',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      jobSearchValue,
      JSON.stringify(jobFilters),
    ),
    enabled: isAuthenticated,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: jobSearchValue,
            sorts: [{ field: 'jobName', direction: 'asc' }],
            filters: jobFilters,
          }),
      ),
  });
  const previousJobsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/page',
      'previous',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      jobSearchValue,
      JSON.stringify(previousJobFilters),
    ),
    enabled: jobsPageQueryGates.shouldLoadJobsPreviousWindowQuery({
      isAuthenticated,
      showDelta,
      comparisonActivated,
      hasCurrentWindowData: currentJobsQuery.data !== undefined,
    }),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: jobSearchValue,
            sorts: [{ field: 'jobName', direction: 'asc' }],
            filters: previousJobFilters,
          }),
      ),
  });
  const currentRunsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.jobRuns,
      'current',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      jobSearchValue,
      JSON.stringify(jobFilters),
    ),
    enabled: isAuthenticated,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobRuns(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: jobSearchValue,
            sorts: [{ field: 'startedAt', direction: 'desc' }],
            filters: jobFilters,
          }),
      ),
  });
  const previousRunsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.jobRuns,
      'previous',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      jobSearchValue,
      JSON.stringify(previousJobFilters),
    ),
    enabled: jobsPageQueryGates.shouldLoadJobsPreviousWindowQuery({
      isAuthenticated,
      showDelta,
      comparisonActivated,
      hasCurrentWindowData: currentRunsQuery.data !== undefined,
    }),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobRuns(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: jobSearchValue,
            sorts: [{ field: 'startedAt', direction: 'desc' }],
            filters: previousJobFilters,
          }),
      ),
  });
  const secondaryJobsQueriesEnabled = jobsPageQueryGates.shouldLoadJobsSecondaryQueries({
    isAuthenticated,
    currentJobsSettled: currentJobsQuery.isFetched || currentJobsQuery.isError,
    currentRunsSettled: currentRunsQuery.isFetched || currentRunsQuery.isError,
  });
  const { jobOptions, pipelineOptions, stageOptions } = useJobsPageOptionQueries({
    effectiveProjectId,
    enabled: secondaryJobsQueriesEnabled,
    selectedOrganisationId: selectedOrganisationId ?? null,
    selectedPipelineIds,
    selectedStageNames,
  });

  const currentJobs = useMemo(
    () => filterJobsBySearch(currentJobsQuery.data?.items ?? [], jobSearchValue),
    [currentJobsQuery.data?.items, jobSearchValue],
  );
  const previousJobs = useMemo(
    () => filterJobsBySearch(previousJobsQuery.data?.items ?? [], jobSearchValue),
    [previousJobsQuery.data?.items, jobSearchValue],
  );
  const currentRuns = useMemo(
    () => filterJobRunsBySearch(currentRunsQuery.data?.items ?? [], jobSearchValue),
    [currentRunsQuery.data?.items, jobSearchValue],
  );
  const previousRuns = useMemo(
    () => filterJobRunsBySearch(previousRunsQuery.data?.items ?? [], jobSearchValue),
    [previousRunsQuery.data?.items, jobSearchValue],
  );
  const showPreviousWindowComparison = jobsPageQueryGates.shouldShowJobsPreviousWindowComparison({
    showDelta,
    hasPreviousJobsData: previousJobsQuery.data !== undefined,
    hasPreviousRunsData: previousRunsQuery.data !== undefined,
  });
  const isPreviousWindowLoading =
    showDelta &&
    ((previousJobsQuery.isFetching && previousJobsQuery.data === undefined) ||
      (previousRunsQuery.isFetching && previousRunsQuery.data === undefined));
  const effectiveCurrentRuns = useMemo(
    () => (currentRuns.length > 0 ? currentRuns : buildFallbackJobRuns(currentJobs)),
    [currentJobs, currentRuns],
  );
  const effectivePreviousRuns = useMemo(
    () => (previousRuns.length > 0 ? previousRuns : buildFallbackJobRuns(previousJobs)),
    [previousJobs, previousRuns],
  );
  const countedFilterOptions = useJobsPageFilterOptionCounts({
    currentJobs,
    enabled: secondaryJobsQueriesEnabled,
    effectiveProjectId,
    isAuthenticated,
    jobOptions,
    jobSearchValue,
    pipelineOptions,
    rangeEndIso,
    rangeStartIso,
    selectedEndDay,
    selectedJobNames,
    selectedOrganisationId: selectedOrganisationId ?? null,
    selectedPipelineIds,
    selectedStageNames,
    selectedStartDay,
    selectedStatuses,
    selectedTags,
    stageOptions,
    tagOptions,
  });

  useEffect(() => {
    const availableStageNames = new Set(stageOptions.map((option) => option.value));
    setSelectedStageNames((current) =>
      current.filter((stageName) => availableStageNames.has(stageName)),
    );
  }, [stageOptions]);

  useEffect(() => {
    const availableJobNames = new Set(jobOptions.map((option) => option.value));
    setSelectedJobNames((current) => current.filter((jobName) => availableJobNames.has(jobName)));
  }, [jobOptions]);

  const jobCounts = useMemo(() => buildJobCounts(currentJobs), [currentJobs]);
  const previousJobCounts = useMemo(() => buildJobCounts(previousJobs), [previousJobs]);
  const metricOptions = useMemo(buildBuildMetricOptions, []);
  const deltas = useMemo(
    () =>
      showPreviousWindowComparison
        ? ({
            queued: (jobCounts.queued ?? 0) - (previousJobCounts.queued ?? 0),
            running: (jobCounts.running ?? 0) - (previousJobCounts.running ?? 0),
            completed: (jobCounts.completed ?? 0) - (previousJobCounts.completed ?? 0),
            failed: (jobCounts.failed ?? 0) - (previousJobCounts.failed ?? 0),
          } satisfies Partial<Record<BuildStatus, number>>)
        : undefined,
    [jobCounts, previousJobCounts, showPreviousWindowComparison],
  );
  const metricCards = useMemo(
    () =>
      buildJobMetricCards({
        options: metricOptions,
        counts: jobCounts,
        deltas,
        jobTotal: currentJobs.length,
        runTotal: effectiveCurrentRuns.length,
        previousCounts: showPreviousWindowComparison ? previousJobCounts : undefined,
        previousJobTotal: showPreviousWindowComparison ? previousJobs.length : undefined,
        previousRunTotal: showPreviousWindowComparison ? effectivePreviousRuns.length : undefined,
        loading:
          (currentJobsQuery.isFetching && !currentJobsQuery.isError) || isPreviousWindowLoading,
        showDelta: showPreviousWindowComparison,
      }),
    [
      currentJobs.length,
      currentJobsQuery.isError,
      currentJobsQuery.isFetching,
      effectiveCurrentRuns.length,
      deltas,
      jobCounts,
      metricOptions,
      previousJobCounts,
      previousJobs.length,
      effectivePreviousRuns.length,
      isPreviousWindowLoading,
      showPreviousWindowComparison,
    ],
  );
  const currentRunMetrics = useMemo(
    () => buildJobRunMetricSummary(effectiveCurrentRuns, chartDates),
    [chartDates, effectiveCurrentRuns],
  );
  const previousRunMetrics = useMemo(
    () => buildJobRunMetricSummary(effectivePreviousRuns, chartDates),
    [chartDates, effectivePreviousRuns],
  );
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: null,
        previousMetrics: null,
        thresholds: thresholds.jobs,
        frequencyOverride: currentRunMetrics.frequencyPerDay,
        previousFrequencyOverride: showPreviousWindowComparison
          ? previousRunMetrics.frequencyPerDay
          : undefined,
        avgDurationOverride: currentRunMetrics.avgDurationSeconds,
        previousAvgDurationOverride: showPreviousWindowComparison
          ? previousRunMetrics.avgDurationSeconds
          : undefined,
        passRateOverride: currentRunMetrics.passRate,
        previousPassRateOverride: showPreviousWindowComparison
          ? previousRunMetrics.passRate
          : undefined,
        showDelta: showPreviousWindowComparison,
      }),
    [
      currentRunMetrics.avgDurationSeconds,
      currentRunMetrics.frequencyPerDay,
      currentRunMetrics.passRate,
      previousRunMetrics.avgDurationSeconds,
      previousRunMetrics.frequencyPerDay,
      previousRunMetrics.passRate,
      showPreviousWindowComparison,
      thresholds.jobs,
    ],
  );
  const latestRunDay = resolveLatestBuildDay(effectiveCurrentRuns);
  const visibleStatuses = useMemo(
    () => resolveVisibleBuildStatuses(selectedStatuses),
    [selectedStatuses],
  );
  const statusSeries = useMemo(() => buildJobStatusSeries(visibleStatuses), [visibleStatuses]);
  const statusCountsByDate = useMemo(
    () => buildJobStatusCountsByDate(effectiveCurrentRuns),
    [effectiveCurrentRuns],
  );
  const metricBucketsByDate = useMemo(
    () => buildJobRunMetricBuckets(effectiveCurrentRuns),
    [effectiveCurrentRuns],
  );
  const chartToolbarState = useMemo(
    () => ({
      ...buildMetricsChartToolbarState({
        search: jobSearchValue,
        setSearch: setJobSearchValue,
        selectedStatuses,
        setSelectedStatuses,
        selectedTags,
        setSelectedTags,
        noRowsText: formatRangeWindowNoDataText(
          getEmptyStateMessage('entity.noneFound', { entity: 'jobRun' }),
          range,
          Boolean(selectedStartDay ?? selectedEndDay),
        ),
        dateQuickFilter:
          range === '14d' || range === '30d' || range === '90d' || range === 'latest'
            ? range
            : '7d',
        onDateQuickFilterChange: activateComparisonForRange,
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        onFromDateChange: handleStartDayChange,
        onToDateChange: handleEndDayChange,
      }),
      selectedEntities: selectedStageNames,
      setSelectedEntities: setSelectedStageNames,
      entityOptions: countedFilterOptions.stageOptions,
      entityLabel: 'Stage',
      selectedItems: selectedJobNames,
      setSelectedItems: setSelectedJobNames,
      itemOptions: countedFilterOptions.jobOptions,
      itemLabel: 'Job',
      selectedSuites: selectedPipelineIds,
      setSelectedSuites: setSelectedPipelineIds,
      suiteOptions: countedFilterOptions.pipelineOptions,
      suiteLabel: 'Pipeline',
      selectedProjects,
      setSelectedProjects: handleProjectsChange,
      projectOptions: showProjectFilter ? projectFilterOptions : undefined,
    }),
    [
      activateComparisonForRange,
      countedFilterOptions,
      handleProjectsChange,
      handleEndDayChange,
      handleStartDayChange,
      jobSearchValue,
      projectFilterOptions,
      range,
      selectedEndDay,
      selectedProjects,
      selectedJobNames,
      selectedPipelineIds,
      selectedStageNames,
      selectedStartDay,
      selectedStatuses,
      selectedTags,
      showProjectFilter,
    ],
  );
  const metricSectionProps = useMemo(
    (): StackedMetricsStatusChartSectionProps<BuildStatus> => ({
      searchAriaLabel: 'Search jobs',
      noRowsBaseText: getEmptyStateMessage('entity.noneFound', { entity: 'jobRun' }),
      scope: 'job',
      projectId: effectiveProjectId,
      availableTags: countedFilterOptions.tagOptions,
      statusOptions: buildStatusOptions,
      metricCards,
      loading: currentJobsQuery.isLoading && currentJobs.length === 0,
      toolbarState: chartToolbarState,
      modelOverride: buildMetricsChartModelFromBuckets({
        statusCountsByDate,
        visibleStatuses,
        statusSeries,
        metricBucketsByDate,
        chartDates,
        metricValueItems,
      }),
      suppressAnimation: searchParams.get('loadState') === 'loading',
      showPoints: false,
      includeLatestQuickFilter: true,
    }),
    [
      chartDates,
      currentJobs.length,
      currentJobsQuery.isLoading,
      chartToolbarState,
      metricBucketsByDate,
      metricCards,
      metricValueItems,
      searchParams,
      effectiveProjectId,
      statusCountsByDate,
      statusSeries,
      tagOptions,
      visibleStatuses,
    ],
  );

  const builtHistoryRows = useMemo(
    () => buildJobHistoryRows(currentJobs, isLatestMode),
    [currentJobs, isLatestMode],
  );
  const historyRows = useMemo(
    () =>
      sortHistoryRows(builtHistoryRows, historySortKey, historySortDirection).slice(
        historyPage * historyPageSize,
        historyPage * historyPageSize + historyPageSize,
      ),
    [builtHistoryRows, historyPage, historyPageSize, historySortDirection, historySortKey],
  );
  const historyNoRowsText = formatRangeWindowNoDataText(
    getEmptyStateMessage('entity.noneFound', { entity: 'jobRun' }),
    range,
    false,
  );
  const historyDateRangeLabel = formatSelectedDateRangeLabel(selectedStartDay, selectedEndDay);
  const showPageSkeleton =
    searchParams.get('pageLoadState') === 'loading' ||
    (isAuthenticated && currentJobsQuery.isLoading && currentJobs.length === 0);
  const isKnownEmpty =
    !showPageSkeleton &&
    searchParams.get('loadState') !== 'loading' &&
    (currentJobsQuery.data?.total ?? 0) === 0;

  const historyPageResetKey = useMemo(
    () =>
      JSON.stringify({
        effectiveProjectId,
        jobFilters,
        jobSearchValue,
        historySortDirection,
        historySortKey,
        selectedOrganisationId,
      }),
    [
      effectiveProjectId,
      jobFilters,
      jobSearchValue,
      historySortDirection,
      historySortKey,
      selectedOrganisationId,
    ],
  );
  useDailyHistoryPanelPageReset({ resetKey: historyPageResetKey, setPage: setHistoryPage });

  return {
    showPageSkeleton,
    isKnownEmpty,
    metricSectionProps,
    historyRows,
    historyDayKeys,
    historyLoading:
      isHistoryTransitionLoading || (currentJobsQuery.isFetching && builtHistoryRows.length === 0),
    historyHasError: currentJobsQuery.isError,
    historyNoRowsText,
    historyDateRangeLabel,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    historyTotalCount: builtHistoryRows.length,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
    tablePanelProps: {
      selectedProjectId: effectiveProjectId,
      selectedOrganisationId: selectedOrganisationId ?? null,
      tagOptions,
      pipelineOptions: countedFilterOptions.pipelineOptions,
      stageOptions: countedFilterOptions.stageOptions,
      jobOptions: countedFilterOptions.jobOptions,
      activeTab,
      onActiveTabChange: setActiveTab,
      dateQuickFilter:
        range === '14d' || range === '30d' || range === '90d' || range === 'latest' ? range : '7d',
      onDateQuickFilterChange: activateComparisonForRange,
      fromDate: selectedStartDay ? dayjs(selectedStartDay) : null,
      toDate: selectedEndDay ? dayjs(selectedEndDay) : null,
      latestQueryStartDay: latestRunDay,
      latestQueryEndDay: latestRunDay,
      onFromDateChange: (next) => {
        handleStartDayChange(next?.format('YYYY-MM-DD') ?? null);
      },
      onToDateChange: (next) => {
        handleEndDayChange(next?.format('YYYY-MM-DD') ?? null);
      },
      jobSearchValue,
      onJobSearchChange: setJobSearchValue,
      jobColumnVisibilityModel,
      onJobColumnVisibilityModelChange: setJobColumnVisibilityModel,
      jobSortKey,
      jobSortDirection,
      onJobSortChange: (nextSortKey, nextSortDirection) => {
        setJobSortKey(nextSortKey);
        setJobSortDirection(nextSortDirection);
      },
      runSearchValue,
      onRunSearchChange: setRunSearchValue,
      runColumnVisibilityModel,
      onRunColumnVisibilityModelChange: setRunColumnVisibilityModel,
      runSortKey,
      runSortDirection,
      onRunSortChange: (nextSortKey, nextSortDirection) => {
        setRunSortKey(nextSortKey);
        setRunSortDirection(nextSortDirection);
      },
      filterStatuses: selectedStatuses,
      onFilterStatusesChange: setSelectedStatuses,
      filterPipelineIds: selectedPipelineIds,
      onFilterPipelineIdsChange: setSelectedPipelineIds,
      filterStageNames: selectedStageNames,
      onFilterStageNamesChange: setSelectedStageNames,
      filterJobNames: selectedJobNames,
      onFilterJobNamesChange: setSelectedJobNames,
      filterTags: selectedTags,
      onFilterTagsChange: setSelectedTags,
      jobSummaryCount: currentJobs.length,
      runSummaryCount: effectiveCurrentRuns.length,
      runRows: effectiveCurrentRuns,
    },
  };
};
